/**
 * Invoices of the project: start one from the quote (or from scratch), fill in the buyer and the
 * lines, check it, issue it — the invoice is then kept in the project, numbered from the series
 * of the project's workspace on the server (one continuous series per company), or of this device
 * for a project outside a workspace, and never changed. An issued invoice is corrected with a credit note
 * (all or some of its lines). Each document downloads as a PDF (with a SEPA payment QR code for
 * an invoice) and as a Peppol BIS Billing 3.0 file for e-invoicing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  newId,
  type Invoice,
  type InvoiceLine,
  type InvoiceParty,
  type Op,
  type Project,
  type VatRegime,
} from '@elec/domain';
import { localeTag, contentLocale, type Locale, type MessageKey, type Translate } from '@elec/i18n';
import { loadPriceList, type Platform } from '@elec/platform';
import type { CloudClient } from '@elec/sync';
import type * as Exporters from '@elec/export';
import { availableSources, loadServerPrices, ordered, preferredOf, useReferenceOf } from './priceSources.ts';
import { Dialog } from './ui.tsx';

type X = typeof Exporters;

/** The installer's invoicing details and invoice series, kept on this device. */
export interface SellerProfile {
  party: Invoice['seller'];
  /** Series prefix, e.g. "2026-". */
  prefix: string;
  /** Next number in the series. */
  next: number;
  /** Payment term (days). */
  termDays: number;
}

const PREF = 'invoiceSeller';
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, d: number) => {
  const t = new Date(`${iso}T12:00:00`);
  t.setDate(t.getDate() + d);
  return t.toISOString().slice(0, 10);
};

export function loadProfile(platform: Platform, project: Project): SellerProfile {
  const year = new Date().getFullYear();
  const blank: SellerProfile = {
    party: {
      name: project.meta.company,
      street: '',
      postcode: '',
      city: '',
      country: 'BE',
      vatNumber: '',
      peppolId: '',
      email: '',
      iban: '',
      bic: '',
    },
    prefix: `${year}-`,
    next: 1,
    termDays: 30,
  };
  try {
    const v = JSON.parse(platform.getPref(PREF) ?? 'null') as SellerProfile | null;
    if (!v) return blank;
    // A new year starts a new series when the prefix is the year.
    if (/^\d{4}-$/.test(v.prefix) && Number(v.prefix.slice(0, 4)) < year)
      return { ...v, prefix: `${year}-`, next: 1 };
    return { ...blank, ...v, party: { ...blank.party, ...v.party } };
  } catch {
    return blank;
  }
}

const UNITS = ['C62', 'MTR', 'HUR'] as const;

function PartyFields(props: {
  t: Translate;
  value: InvoiceParty;
  onChange(p: InvoiceParty): void;
  id: string;
  bank?: { iban: string; bic: string; onChange(b: { iban: string; bic: string }): void };
}) {
  const { t, value: p } = props;
  const field = (
    k: keyof InvoiceParty,
    label: MessageKey,
    extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {},
  ) => (
    <div className="field">
      <label htmlFor={`${props.id}-${k}`}>{t(label)}</label>
      <input
        id={`${props.id}-${k}`}
        value={p[k]}
        onChange={(e) => props.onChange({ ...p, [k]: e.target.value })}
        data-testid={`${props.id}-${k}`}
        {...extra}
      />
    </div>
  );
  return (
    <div className="form-grid">
      {field('name', 'invoice.name')}
      {field('vatNumber', 'invoice.vatNumber', { placeholder: 'BE0123456789' })}
      {field('street', 'invoice.street')}
      <div className="field-row">
        {field('postcode', 'invoice.postcode')}
        {field('city', 'invoice.city')}
        {field('country', 'invoice.country', { maxLength: 2, style: { width: 48 } })}
      </div>
      {field('email', 'invoice.email', { type: 'email' })}
      {field('peppolId', 'invoice.peppolId', { placeholder: '0208:0123456789' })}
      {props.bank && (
        <>
          <div className="field">
            <label htmlFor={`${props.id}-iban`}>IBAN</label>
            <input
              id={`${props.id}-iban`}
              value={props.bank.iban}
              onChange={(e) => props.bank!.onChange({ iban: e.target.value, bic: props.bank!.bic })}
              data-testid={`${props.id}-iban`}
            />
          </div>
          <div className="field">
            <label htmlFor={`${props.id}-bic`}>BIC</label>
            <input
              id={`${props.id}-bic`}
              value={props.bank.bic}
              onChange={(e) => props.bank!.onChange({ iban: props.bank!.iban, bic: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  );
}

export function InvoiceDialog(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  platform: Platform;
  baseName: string;
  readOnly: boolean;
  load(): Promise<X>;
  save(bytes: Uint8Array, name: string, ext: string, mime: string): Promise<void>;
  dispatch(label: string, ops: Op[]): void;
  notify(msg: string): void;
  /** Start a new invoice from this quote. */
  fromQuote?: Exporters.Quote | undefined;
  /** Signed-in account: the wholesalers' price lists kept on the server. */
  client?: CloudClient | null;
  /**
   * Workspace of the open cloud project: its invoice series is kept on the server. Offline, the
   * project still belongs to it: issuing waits for the connection rather than numbering here.
   */
  workspace?: { id: string; name: string; role: 'owner' | 'editor' | 'viewer'; client: CloudClient } | null;
  /** The server can send email: invoices can be emailed to the client. */
  canEmail?: boolean;
  onClose(): void;
}) {
  const { t, project, locale } = props;
  const [x, setX] = useState<X | null>(null);
  const [profile, setProfile] = useState(() => loadProfile(props.platform, project));
  const [draft, setDraft] = useState<Invoice | null>(null);
  const [depositPct, setDepositPct] = useState(30);
  // Workspace series: the next number, read from the server (null until known or when offline).
  const team = props.workspace ?? null;
  const [teamNext, setTeamNext] = useState<number | null>(null);
  const [startAt, setStartAt] = useState('');
  // Emailing an issued document to the client.
  const [mailing, setMailing] = useState<{ inv: Invoice; to: string; message: string; xml: boolean } | null>(
    null,
  );
  const [mailBusy, setMailBusy] = useState(false);
  // Billit (docs/decisions/0013, step 7): the account's connection, and a document being handed on.
  const [billit, setBillit] = useState<{ configured: boolean; connected: boolean } | null>(null);
  const [handing, setHanding] = useState<{ inv: Invoice; peppol: boolean } | null>(null);
  const [billitBusy, setBillitBusy] = useState(false);
  const [billitStatus, setBillitStatus] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!props.client) return;
    props.client
      .billitStatus()
      .then(setBillit)
      .catch(() => setBillit(null));
  }, [props.client]);
  // After "Connect": Billit's page opens elsewhere; look for the connection for a few minutes.
  const [waitingBillit, setWaitingBillit] = useState(false);
  useEffect(() => {
    if (!waitingBillit || !props.client) return;
    const client = props.client;
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      void client
        .billitStatus()
        .then((st) => {
          setBillit(st);
          if (st.connected || tries > 60) setWaitingBillit(false);
        })
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(timer);
  }, [waitingBillit, props.client]);
  const connectBillit = async () => {
    if (!props.client) return;
    try {
      const { url } = await props.client.billitConnect();
      await props.platform.openUrl(url);
      setWaitingBillit(true);
    } catch (e) {
      props.notify(t('billit.failed', { msg: (e as Error).message }));
    }
  };
  const handOn = async () => {
    if (!x || !handing || !props.client) return;
    const { inv } = handing;
    setBillitBusy(true);
    try {
      const pdf = x.toBase64(await x.invoicePdf(inv, locale));
      const r = await props.client.billitSend(inv, contentLocale(locale), pdf, handing.peppol);
      props.dispatch(t('billit.tx', { n: inv.number }), [
        {
          op: 'update',
          coll: 'invoices',
          id: inv.id,
          patch: { billit: { orderId: r.orderId, at: new Date().toISOString(), peppol: r.sent } },
        },
      ]);
      props.notify(r.sent ? t('billit.sentPeppol', { n: inv.number }) : t('billit.sent', { n: inv.number }));
      setHanding(null);
    } catch (e) {
      const msg = (e as Error).message;
      // The connection was refused by Billit (revoked): connect again.
      if ((e as { status?: number }).status === 409) setBillit((b) => (b ? { ...b, connected: false } : b));
      props.notify(t('billit.failed', { msg }));
    } finally {
      setBillitBusy(false);
    }
  };
  const readBillitStatus = async (inv: Invoice) => {
    if (!props.client || !inv.billit) return;
    try {
      const st = await props.client.billitOrder(inv.billit.orderId);
      const parts = [
        st.status ? t(`billit.status.${st.status}` as MessageKey, {}) : '',
        st.delivery ? `Peppol: ${st.delivery}` : '',
        st.paid ? t('billit.paid') : '',
      ].filter((p) => p && !p.startsWith('billit.'));
      setBillitStatus((m) => ({ ...m, [inv.id]: parts.join(' · ') || (st.status ?? '?') }));
    } catch (e) {
      props.notify(t('billit.failed', { msg: (e as Error).message }));
    }
  };
  useEffect(() => {
    if (!team) return;
    team.client
      .invoiceSeries(team.id, profile.prefix)
      .then((r) => setTeamNext(r.next))
      .catch(() => setTeamNext(null));
  }, [team?.id, profile.prefix]);
  /** The number a new document will get (a preview in a workspace: the server gives it on issue). */
  const nextNumber = (ex: X) =>
    team
      ? teamNext
        ? ex.invoiceNumber(profile.prefix, teamNext)
        : ''
      : ex.invoiceNumber(profile.prefix, profile.next);
  const issued = useMemo(
    () =>
      Object.values(project.invoices ?? {}).sort(
        (a, b) => b.issueDate.localeCompare(a.issueDate) || b.number.localeCompare(a.number),
      ),
    [project.invoices],
  );
  const money = (n: number) => n.toLocaleString(localeTag(locale), { style: 'currency', currency: 'EUR' });

  const newDraft = (ex: X, lines: InvoiceLine[], vat: VatRegime): Invoice => {
    const number = nextNumber(ex);
    const issueDate = today();
    return {
      id: newId(),
      number,
      issueDate,
      dueDate: addDays(issueDate, profile.termDays),
      seller: profile.party,
      buyer: ex.buyerFromProject(project),
      lines,
      vat,
      paymentRef: ex.structuredReference(number.replace(/\D/g, '')),
      orderRef: '',
      note: '',
    };
  };
  useEffect(() => {
    if (team && x && draft && teamNext)
      setDraft({ ...draft, number: x.invoiceNumber(profile.prefix, teamNext) });
  }, [teamNext]);
  const startCredit = (inv: Invoice) => {
    if (!x) return;
    setDraft(x.creditNoteFrom(inv, newId(), nextNumber(x), today()));
  };
  useEffect(() => {
    void props.load().then((ex) => {
      setX(ex);
      if (props.fromQuote)
        setDraft(newDraft(ex, ex.linesFromQuote(props.fromQuote), ex.vatOfQuote(props.fromQuote)));
    });
  }, []);

  const startFromQuote = async () => {
    if (!x || !draft) return;
    // Same prices as the quote: the preferred source first, then the others.
    const [own, server] = await Promise.all([
      loadPriceList(),
      loadServerPrices(x, project, props.client ?? null),
    ]);
    const sources = ordered(
      availableSources(x, own, server.data, useReferenceOf(props.platform), ''),
      preferredOf(props.platform),
    );
    const quote = x.buildQuote(
      project,
      { sources: sources.map((s) => ({ id: s.id, entries: s.entries })) },
      undefined,
      locale,
    );
    setDraft({ ...draft, lines: [...draft.lines, ...x.linesFromQuote(quote)] });
  };
  // Stable row keys (lines are plain data): an edited line keeps its row, a removed one takes its own.
  const keys = useRef(new WeakMap<InvoiceLine, string>());
  const keyOf = (l: InvoiceLine) => {
    let k = keys.current.get(l);
    if (!k) keys.current.set(l, (k = newId()));
    return k;
  };
  const setLine = (i: number, l: Partial<InvoiceLine>) =>
    draft &&
    setDraft({
      ...draft,
      lines: draft.lines.map((o, j) => {
        if (j !== i) return o;
        const n = { ...o, ...l };
        keys.current.set(n, keyOf(o));
        return n;
      }),
    });
  const num = (s: string) => {
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const download = async (inv: Invoice, kind: 'pdf' | 'xml') => {
    if (!x) return;
    const name = `${t('invoice.file')} ${inv.number}`.replace(/[\\/:*?"<>|]+/g, '-');
    try {
      if (kind === 'pdf')
        await props.save(await x.invoicePdf(inv, locale), `${name}.pdf`, 'pdf', 'application/pdf');
      else
        await props.save(
          new TextEncoder().encode(x.invoiceUbl(inv, locale)),
          `${name} - Peppol.xml`,
          'xml',
          'application/xml',
        );
    } catch (e) {
      props.notify(t('error.export', { msg: (e as Error).message }));
    }
  };

  const startMail = (inv: Invoice) =>
    setMailing({
      inv,
      to: inv.buyer.email,
      message: '',
      xml: !!x?.peppolReady(inv) && !!inv.buyer.vatNumber,
    });
  const sendMail = async () => {
    if (!x || !mailing || !props.client) return;
    const { inv } = mailing;
    setMailBusy(true);
    try {
      const pdf = await x.invoicePdf(inv, locale);
      await props.client.emailInvoice({
        to: mailing.to.trim(),
        replyTo: inv.seller.email,
        seller: inv.seller.name,
        number: inv.number,
        kind: inv.kind === 'credit-note' ? 'credit-note' : inv.deposit ? 'deposit' : 'invoice',
        total: money(x.invoiceTotals(inv).total),
        dueDate: inv.kind === 'credit-note' ? '' : fmtDate(inv.dueDate),
        message: mailing.message,
        locale,
        pdf: x.toBase64(pdf),
        ...(mailing.xml ? { xml: x.toBase64(new TextEncoder().encode(x.invoiceUbl(inv, locale))) } : {}),
      });
      props.notify(t('invoice.emailSent', { n: inv.number, to: mailing.to.trim() }));
      setMailing(null);
    } catch (e) {
      props.notify(t('invoice.emailFailed', { msg: (e as Error).message }));
    } finally {
      setMailBusy(false);
    }
  };
  const issue = async () => {
    if (!x || !draft) return;
    let number = draft.number;
    let paymentRef = draft.paymentRef;
    if (team) {
      // The workspace gives the number now: each number once, whoever invoices.
      try {
        const r = await team.client.takeInvoiceNumber(team.id, profile.prefix);
        number = r.number;
        setTeamNext(r.n + 1);
      } catch {
        return props.notify(t('invoice.seriesOffline', { team: team.name }));
      }
      // The structured communication was made from the previewed number: from the real one.
      if (paymentRef && paymentRef === x.structuredReference(draft.number.replace(/\D/g, '')))
        paymentRef = x.structuredReference(number.replace(/\D/g, ''));
    }
    const inv: Invoice = {
      ...draft,
      number,
      paymentRef,
      seller: { ...draft.seller, vatNumber: x.formatVat(draft.seller.vatNumber) },
      buyer: {
        ...draft.buyer,
        vatNumber: draft.buyer.vatNumber.trim() ? x.formatVat(draft.buyer.vatNumber) : '',
      },
    };
    if (issued.some((o) => o.number === inv.number))
      return props.notify(t('invoice.duplicate', { n: inv.number }));
    props.dispatch(t('invoice.txIssue', { n: inv.number }), [{ op: 'add', coll: 'invoices', value: inv }]);
    // The series moves on past the number used (also when it was typed by hand); a workspace's
    // series is the server's, only the seller details are kept here.
    const used = Number(/(\d+)$/.exec(inv.number)?.[1] ?? profile.next);
    const next: SellerProfile = team
      ? { ...profile, party: inv.seller }
      : { ...profile, party: inv.seller, next: Math.max(profile.next, used + 1) };
    setProfile(next);
    props.platform.setPref(PREF, JSON.stringify(next));
    setDraft(null);
  };

  const fmtDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(localeTag(locale));
  /** Deposit invoices this draft can still deduct. */
  const open = useMemo(() => (x ? x.openDeposits(issued) : []), [x, issued]);
  const makeDeposit = () => {
    if (!x || !draft) return;
    const label = t('invoice.depositLine', { pct: depositPct, ref: draft.orderRef || project.meta.title });
    setDraft({ ...draft, deposit: true, lines: x.depositLines(draft, depositPct, label) });
  };
  const deduct = () => {
    if (!x || !draft) return;
    setDraft(
      x.deductDeposits(draft, open, (d) =>
        t('invoice.depositDeducted', { n: d.number, date: fmtDate(d.issueDate) }),
      ),
    );
  };
  const problems = draft && x ? x.invoiceProblems(draft) : [];
  const totals = draft && x ? x.invoiceTotals(draft) : null;
  const peppol = draft && x ? x.peppolReady(draft) : false;

  return (
    <Dialog
      title={
        draft
          ? draft.kind === 'credit-note'
            ? t('invoice.newCreditNote')
            : t('invoice.new')
          : t('invoice.title')
      }
      wide
      onClose={props.onClose}
      actions={
        draft ? (
          <>
            <button onClick={() => setDraft(null)}>{t('dialog.cancel')}</button>
            <button
              className="primary"
              disabled={problems.length > 0 || props.readOnly}
              onClick={() => void issue()}
              data-testid="invoice-issue"
            >
              {t('invoice.issue')}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => x && setDraft(newDraft(x, [], 'S21'))}
              disabled={!x || props.readOnly}
              data-testid="invoice-new"
            >
              {t('invoice.new')}
            </button>
            <button className="primary" onClick={props.onClose}>
              {t('dialog.close')}
            </button>
          </>
        )
      }
    >
      {!draft ? (
        <>
          <p className="muted">{t('invoice.intro')}</p>
          {billit?.configured && (
            <p className="inv-billit muted small" data-testid="billit-banner">
              {billit.connected ? (
                <>
                  {t('billit.connectedTo')}{' '}
                  <button
                    className="link"
                    onClick={() =>
                      void props.client
                        ?.billitDisconnect()
                        .then(() => setBillit({ ...billit, connected: false }))
                    }
                  >
                    {t('billit.disconnect')}
                  </button>
                </>
              ) : (
                <>
                  {t('billit.pitch')}{' '}
                  <button onClick={() => void connectBillit()} data-testid="billit-connect">
                    {waitingBillit ? t('billit.waiting') : t('billit.connect')}
                  </button>
                </>
              )}
            </p>
          )}
          {team?.role === 'owner' && teamNext === 1 && (
            <p className="inv-series" data-testid="invoice-series-start">
              {t('invoice.seriesStart', { team: team.name, prefix: profile.prefix })}{' '}
              <input
                inputMode="numeric"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value.replace(/\D/g, ''))}
                style={{ width: 72 }}
                aria-label={t('invoice.seriesStartLabel')}
                data-testid="invoice-series-next"
              />{' '}
              <button
                disabled={!Number(startAt)}
                onClick={() =>
                  void team.client
                    .setInvoiceSeries(team.id, profile.prefix, Number(startAt))
                    .then((r) => setTeamNext(r.next))
                    .catch((e: Error) => props.notify(e.message))
                }
                data-testid="invoice-series-set"
              >
                {t('invoice.seriesSet')}
              </button>
            </p>
          )}
          {issued.length === 0 ? (
            <p className="muted" data-testid="invoice-none">
              {t('invoice.none')}
            </p>
          ) : (
            <div className="quote-table" tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    <th>{t('invoice.number')}</th>
                    <th>{t('invoice.issueDate')}</th>
                    <th>{t('invoice.buyer')}</th>
                    <th className="num">{t('invoice.total')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody data-testid="invoice-list">
                  {issued.map((inv) => (
                    <tr key={inv.id} data-kind={inv.kind ?? 'invoice'}>
                      <td className="mono">
                        {inv.number}
                        {inv.kind === 'credit-note' && (
                          <span className="muted small"> · {t('invoice.kindCredit')}</span>
                        )}
                        {inv.deposit && <span className="muted small"> · {t('invoice.kindDeposit')}</span>}
                      </td>
                      <td>{new Date(`${inv.issueDate}T12:00:00`).toLocaleDateString(localeTag(locale))}</td>
                      <td>{inv.buyer.name}</td>
                      <td className="num">
                        {x ? money((inv.kind === 'credit-note' ? -1 : 1) * x.invoiceTotals(inv).total) : ''}
                      </td>
                      <td className="inv-actions">
                        <button onClick={() => void download(inv, 'pdf')} data-testid="invoice-pdf">
                          PDF
                        </button>
                        <button
                          onClick={() => void download(inv, 'xml')}
                          disabled={!x?.peppolReady(inv)}
                          title={x?.peppolReady(inv) ? t('invoice.peppolHint') : t('invoice.peppolMissing')}
                          data-testid="invoice-ubl"
                        >
                          Peppol (UBL)
                        </button>
                        {billit?.connected && !inv.billit && (
                          <button
                            onClick={() => setHanding({ inv, peppol: !!x?.peppolReady(inv) })}
                            disabled={!x || props.readOnly}
                            data-testid="invoice-billit"
                          >
                            Billit…
                          </button>
                        )}
                        {inv.billit && (
                          <button
                            className="link"
                            onClick={() => void readBillitStatus(inv)}
                            title={t('billit.statusHint')}
                            data-testid="invoice-billit-status"
                          >
                            Billit #{inv.billit.orderId}
                          </button>
                        )}
                        {inv.billit && billitStatus[inv.id] && (
                          <span className="muted small inv-billit-state" data-testid="invoice-billit-state">
                            {billitStatus[inv.id]}
                          </span>
                        )}
                        {props.client && props.canEmail && (
                          <button onClick={() => startMail(inv)} disabled={!x} data-testid="invoice-email">
                            {t('invoice.emailAction')}
                          </button>
                        )}
                        {inv.kind !== 'credit-note' && (
                          <button
                            onClick={() => startCredit(inv)}
                            disabled={!x || props.readOnly}
                            title={t('invoice.creditHint')}
                            data-testid="invoice-credit"
                          >
                            {t('invoice.creditNote')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {handing && (
            <div className="inv-mail" data-testid="invoice-billit-form">
              <h4>{t('billit.title', { n: handing.inv.number })}</h4>
              <p className="muted small">{t('billit.explain')}</p>
              <label className="check">
                <input
                  type="checkbox"
                  checked={handing.peppol}
                  disabled={!x?.peppolReady(handing.inv)}
                  onChange={(e) => setHanding({ ...handing, peppol: e.target.checked })}
                  data-testid="invoice-billit-peppol"
                />{' '}
                {x?.peppolReady(handing.inv) ? t('billit.peppolNow') : t('billit.noPeppol')}
              </label>
              <div className="inv-mail-actions">
                <button onClick={() => setHanding(null)}>{t('dialog.cancel')}</button>
                <button
                  className="primary"
                  disabled={billitBusy}
                  onClick={() => void handOn()}
                  data-testid="invoice-billit-send"
                >
                  {t('billit.send')}
                </button>
              </div>
            </div>
          )}
          {mailing && (
            <div className="inv-mail" data-testid="invoice-mail-form">
              <h4>{t('invoice.emailTitle', { n: mailing.inv.number })}</h4>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="inv-mail-to">{t('invoice.emailTo')}</label>
                  <input
                    id="inv-mail-to"
                    type="email"
                    value={mailing.to}
                    onChange={(e) => setMailing({ ...mailing, to: e.target.value })}
                    data-testid="invoice-mail-to"
                  />
                </div>
                <div className="field">
                  <label htmlFor="inv-mail-msg">{t('invoice.emailMessage')}</label>
                  <textarea
                    id="inv-mail-msg"
                    rows={3}
                    maxLength={1500}
                    value={mailing.message}
                    onChange={(e) => setMailing({ ...mailing, message: e.target.value })}
                  />
                </div>
              </div>
              <label className="check">
                <input
                  type="checkbox"
                  checked={mailing.xml}
                  disabled={!x?.peppolReady(mailing.inv)}
                  onChange={(e) => setMailing({ ...mailing, xml: e.target.checked })}
                />{' '}
                {t('invoice.emailXml')}
              </label>
              <p className="muted small">
                {t('invoice.emailReply', { email: mailing.inv.seller.email || t('invoice.emailAccount') })}
              </p>
              <div className="inv-mail-actions">
                <button onClick={() => setMailing(null)}>{t('dialog.cancel')}</button>
                <button
                  className="primary"
                  disabled={mailBusy || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailing.to.trim())}
                  onClick={() => void sendMail()}
                  data-testid="invoice-mail-send"
                >
                  {t('invoice.emailSend')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="inv-edit" data-testid="invoice-editor">
          <details className="inv-section" open={!profile.party.vatNumber}>
            <summary>
              <strong>{t('invoice.seller')}</strong> — {draft.seller.name || '…'}{' '}
              <span className="muted small">{t('invoice.sellerKept')}</span>
            </summary>
            <PartyFields
              t={t}
              id="inv-seller"
              value={draft.seller}
              onChange={(p) => setDraft({ ...draft, seller: { ...draft.seller, ...p } })}
              bank={{
                iban: draft.seller.iban,
                bic: draft.seller.bic,
                onChange: (b) => setDraft({ ...draft, seller: { ...draft.seller, ...b } }),
              }}
            />
          </details>
          {draft.creditOf && (
            <p className="muted" data-testid="invoice-credit-of">
              {t('invoice.creditFor', {
                n: draft.creditOf.number,
                date: new Date(`${draft.creditOf.issueDate}T12:00:00`).toLocaleDateString(localeTag(locale)),
              })}
            </p>
          )}
          <h4>{t('invoice.buyer')}</h4>
          <PartyFields
            t={t}
            id="inv-buyer"
            value={draft.buyer}
            onChange={(buyer) => setDraft({ ...draft, buyer })}
          />
          <p className="muted small">{t('invoice.buyerHint')}</p>
          <h4>{t('invoice.details')}</h4>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="inv-number">{t('invoice.number')}</label>
              <input
                id="inv-number"
                value={draft.number}
                readOnly={!!team}
                onChange={(e) => setDraft({ ...draft, number: e.target.value })}
                data-testid="inv-number"
              />
              {team && (
                <span className="muted small" data-testid="inv-number-team">
                  {teamNext
                    ? t('invoice.seriesTeam', { team: team.name })
                    : t('invoice.seriesOffline', { team: team.name })}
                </span>
              )}
            </div>
            <div className="field">
              <label htmlFor="inv-vat">{t('invoice.vat')}</label>
              <select
                id="inv-vat"
                value={draft.vat}
                onChange={(e) => setDraft({ ...draft, vat: e.target.value as VatRegime })}
                data-testid="inv-vat"
              >
                <option value="S21">{t('quote.vat21')}</option>
                <option value="S6">{t('quote.vat6')}</option>
                <option value="AE">{t('invoice.reverseCharge')}</option>
              </select>
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="inv-date">{t('invoice.issueDate')}</label>
                <input
                  id="inv-date"
                  type="date"
                  value={draft.issueDate}
                  onChange={(e) =>
                    e.target.value &&
                    setDraft({
                      ...draft,
                      issueDate: e.target.value,
                      dueDate:
                        draft.kind === 'credit-note'
                          ? e.target.value
                          : addDays(e.target.value, profile.termDays),
                    })
                  }
                />
              </div>
              {draft.kind !== 'credit-note' && (
                <div className="field">
                  <label htmlFor="inv-due">{t('invoice.dueDate')}</label>
                  <input
                    id="inv-due"
                    type="date"
                    value={draft.dueDate}
                    onChange={(e) => e.target.value && setDraft({ ...draft, dueDate: e.target.value })}
                  />
                </div>
              )}
            </div>
            <div className="field">
              <label htmlFor="inv-order">{t('invoice.orderRef')}</label>
              <input
                id="inv-order"
                value={draft.orderRef}
                onChange={(e) => setDraft({ ...draft, orderRef: e.target.value })}
              />
            </div>
            {draft.kind !== 'credit-note' && (
              <div className="field">
                <label htmlFor="inv-ogm">{t('invoice.paymentRef')}</label>
                <input
                  id="inv-ogm"
                  className="mono"
                  value={draft.paymentRef}
                  onChange={(e) => setDraft({ ...draft, paymentRef: e.target.value })}
                />
              </div>
            )}
            <div className="field">
              <label htmlFor="inv-note">{t('invoice.note')}</label>
              <input
                id="inv-note"
                value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              />
            </div>
          </div>
          {x && x.vatMentions(draft, locale) && <p className="muted small">{x.vatMentions(draft, locale)}</p>}

          <div className="inv-lines-head">
            <h4>{t('invoice.lines')}</h4>
            <span className="grow" />
            {draft.kind !== 'credit-note' && !draft.deposit && (
              <button onClick={() => void startFromQuote()} data-testid="invoice-from-quote">
                {t('invoice.addQuoteLines')}
              </button>
            )}
            <button
              onClick={() =>
                setDraft({
                  ...draft,
                  lines: [
                    ...draft.lines,
                    { description: '', reference: '', quantity: 1, unitCode: 'C62', unitPrice: 0 },
                  ],
                })
              }
              data-testid="invoice-add-line"
            >
              + {t('invoice.addLine')}
            </button>
          </div>
          {draft.kind !== 'credit-note' && !draft.deposit && (
            <div className="inv-deposit">
              {draft.lines.length > 0 && !draft.deducts?.length && (
                <span className="field-row">
                  <input
                    inputMode="decimal"
                    aria-label={t('invoice.depositPct')}
                    value={depositPct}
                    onChange={(e) => setDepositPct(Math.max(1, Math.min(100, num(e.target.value) || 0)))}
                    style={{ width: 56 }}
                    data-testid="invoice-deposit-pct"
                  />
                  <span>%</span>
                  <button
                    onClick={makeDeposit}
                    data-testid="invoice-make-deposit"
                    title={t('invoice.depositHint')}
                  >
                    {t('invoice.makeDeposit')}
                  </button>
                </span>
              )}
              {open.length > 0 && !draft.deducts?.length && (
                <button onClick={deduct} data-testid="invoice-deduct">
                  {t('invoice.deductDeposits', { n: open.length })}
                </button>
              )}
            </div>
          )}
          {draft.deposit && (
            <p className="muted small" data-testid="invoice-is-deposit">
              {t('invoice.isDeposit')}
            </p>
          )}
          <div className="quote-table inv-lines">
            <table>
              <thead>
                <tr>
                  <th>{t('quote.colDesc')}</th>
                  <th>{t('quote.colRef')}</th>
                  <th className="num">{t('quote.colQty')}</th>
                  <th>{t('invoice.unit')}</th>
                  <th className="num">{t('quote.colPrice')}</th>
                  {draft.vat !== 'AE' && <th>{t('invoice.vat')}</th>}
                  <th className="num">{t('invoice.amount')}</th>
                  <th />
                </tr>
              </thead>
              <tbody data-testid="invoice-lines">
                {draft.lines.map((l, i) => (
                  <tr key={keyOf(l)} className={l.unitPrice ? '' : 'unpriced'}>
                    <td>
                      <input
                        value={l.description}
                        onChange={(e) => setLine(i, { description: e.target.value })}
                        aria-label={t('quote.colDesc')}
                      />
                    </td>
                    <td>
                      <input
                        className="mono"
                        value={l.reference}
                        onChange={(e) => setLine(i, { reference: e.target.value })}
                        aria-label={t('quote.colRef')}
                      />
                    </td>
                    <td className="num">
                      <input
                        inputMode="decimal"
                        defaultValue={String(l.quantity).replace('.', locale === 'en' ? '.' : ',')}
                        onBlur={(e) => setLine(i, { quantity: num(e.target.value) })}
                        aria-label={t('quote.colQty')}
                      />
                    </td>
                    <td>
                      <select
                        value={l.unitCode}
                        onChange={(e) => setLine(i, { unitCode: e.target.value })}
                        aria-label={t('invoice.unit')}
                      >
                        {UNITS.map((u) => (
                          <option key={u} value={u}>
                            {t(`invoice.unit.${u}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        inputMode="decimal"
                        defaultValue={
                          l.unitPrice ? String(l.unitPrice).replace('.', locale === 'en' ? '.' : ',') : ''
                        }
                        onBlur={(e) => setLine(i, { unitPrice: Math.round(num(e.target.value) * 100) / 100 })}
                        aria-label={t('quote.colPrice')}
                        data-testid="invoice-line-price"
                      />
                    </td>
                    {draft.vat !== 'AE' && (
                      <td>
                        <select
                          value={l.vat ?? draft.vat}
                          onChange={(e) => {
                            const v = e.target.value as VatRegime;
                            // Same as the invoice: no override, so it follows the invoice's rate.
                            setLine(i, { vat: v === draft.vat ? undefined : v });
                          }}
                          aria-label={t('invoice.vat')}
                          data-testid="invoice-line-vat"
                        >
                          <option value="S21">21 %</option>
                          <option value="S6">6 %</option>
                        </select>
                      </td>
                    )}
                    <td className="num">{x ? money(x.lineAmount(l)) : ''}</td>
                    <td>
                      <button
                        className="icon-btn"
                        aria-label={t('tx.delete')}
                        onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totals && totals.breakdown.length > 1 && (
            <p className="muted small inv-totals" data-testid="invoice-vat-breakdown">
              {totals.breakdown
                .map((b) => t('invoice.vatPart', { pct: b.pct, base: money(b.base), vat: money(b.amount) }))
                .join(' · ')}
            </p>
          )}
          {totals && (
            <p className="inv-totals" data-testid="invoice-totals">
              {t('quote.totals', {
                sub: money(totals.net),
                vat: money(totals.vat),
                total: money(totals.total),
              })}
            </p>
          )}
          {problems.length > 0 ? (
            <ul className="warn inv-problems" data-testid="invoice-problems">
              {problems.map((p) => (
                <li key={p}>{t(`invoice.problem.${p}` as MessageKey)}</li>
              ))}
            </ul>
          ) : (
            <p className={peppol ? 'ok-text' : 'muted'} data-testid="invoice-peppol">
              {peppol ? t('invoice.peppolReady') : t('invoice.pdfOnly')}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
